import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';
import { documentService } from '../../../services/documentService';
import { userService } from '../../../services/userService';
import { DocumentVersion } from '../../../types/documents';
import { useToast } from '../../UI/ToastProvider';
import { getWorkflowStatusBadgeClasses, getWorkflowStatusLabel } from '../../../utils/workflowStatusBadge';

interface DocumentVersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
  onRestored?: (newVersion: DocumentVersion) => void;
}

type ActivePane = 'visualizar' | 'comparar';
type Rec = Record<string, unknown>;
type DiffType = 'equal' | 'insert' | 'delete';
type LineDiffType = 'equal' | 'insert' | 'delete' | 'changed';

interface ParsedAttachment { name: string; url?: string; }
interface ParsedVersionPayload {
  title: string;
  status: string;
  updatedAt: string;
  coverImage: string;
  description: string;
  content: string;
  attachments: ParsedAttachment[];
  metadata: Rec;
}
interface DiffSeg { type: DiffType; text: string; }
interface LineDiffRow {
  leftNumber: number | null;
  rightNumber: number | null;
  leftText: string;
  rightText: string;
  type: LineDiffType;
}

const readOnlyModules = { toolbar: false };
const isRecord = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const isHtml = (v: string) => (v || '').trim().startsWith('<') && (v || '').includes('>');
const first = (...vals: unknown[]) => vals.find(v => typeof v === 'string' && v.trim()) as string || '';
const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const norm = (v: string) => (v || '').replace(/\s+/g, ' ').trim();

const statusLabel = (status: string) => getWorkflowStatusLabel(status);

const statusClassName = (status: string) => getWorkflowStatusBadgeClasses(status);

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
};

const parseJson = (value: string): unknown | null => {
  const trimmed = (value || '').trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return null;
  try { return JSON.parse(trimmed); } catch { return null; }
};

const extractContent = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (!isRecord(v)) return '';
  return first(v.content, v.fileContent, v.text, v.html, v.value, v.url, v.storagePath);
};

const extractAttachments = (v: unknown): ParsedAttachment[] => {
  if (!Array.isArray(v)) return [];
  return v.map(item => {
    if (typeof item === 'string') return item.trim() ? { name: item.trim() } : null;
    if (!isRecord(item)) return null;
    const name = first(item.name, item.fileName, item.title, item.storagePath, item.url) || 'Arquivo sem nome';
    const url = first(item.url, item.storagePath, item.downloadUrl, item.fileUrl);
    return { name, url: url || undefined };
  }).filter((x): x is ParsedAttachment => Boolean(x));
};

const parsePayload = (payload: string): ParsedVersionPayload => {
  const fallback: ParsedVersionPayload = {
    title: '', status: '', updatedAt: '', coverImage: '', description: '',
    content: payload || '', attachments: [], metadata: {}
  };
  const parsed = parseJson(payload);
  if (!isRecord(parsed)) return fallback;

  const doc = isRecord(parsed.document) ? parsed.document : {};
  const meta = isRecord(parsed.metadata) ? parsed.metadata : {};
  const docMeta = isRecord(doc.metadata) ? doc.metadata : {};

  return {
    title: first(parsed.title, doc.title, meta.title, docMeta.title),
    status: first(parsed.status, doc.status, meta.status, docMeta.status),
    updatedAt: first(parsed.updatedAt, parsed.lastModifiedAt, doc.updatedAt, meta.updatedAt, meta.lastModifiedAt, docMeta.updatedAt),
    coverImage: first(parsed.coverImage, extractContent(parsed.cover), doc.coverImage, extractContent(doc.cover), meta.coverImage, docMeta.coverImage, meta.coverUrl),
    description: first(parsed.description, doc.description, meta.description, docMeta.description, meta.resume),
    content: first(extractContent(parsed.content), extractContent(parsed.payload), extractContent(doc.content), parsed.body, parsed.text, payload),
    attachments: [...extractAttachments(parsed.attachments), ...extractAttachments(doc.attachments), ...extractAttachments(meta.attachments)],
    metadata: { ...docMeta, ...meta }
  };
};

const parsePayloadWithFallback = (
  payload: string,
  fallback: { title?: string; status?: string; updatedAt?: string } = {}
): ParsedVersionPayload => {
  const parsed = parsePayload(payload);
  return {
    ...parsed,
    title: parsed.title || fallback.title || '',
    status: parsed.status || fallback.status || '',
    updatedAt: parsed.updatedAt || fallback.updatedAt || ''
  };
};

const plainText = (html: string) => {
  if (!html || !isHtml(html)) return html || '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const blockTags = new Set([
      'P', 'DIV', 'LI',
      'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'TR',
      'UL', 'OL',
      'BLOCKQUOTE',
      'PRE'
    ]);

    let out = '';
    const append = (s: string) => { out += s; };
    const ensureNewline = () => {
      if (!out.endsWith('\n')) append('\n');
    };

    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.nodeValue ?? '';
        if (!raw || !raw.replace(/\s+/g, '').length) return;
        append(raw.replace(/\s+/g, ' '));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as HTMLElement;
      const tag = el.tagName;
      if (tag === 'BR') {
        append('\n');
        return;
      }
      for (const child of Array.from(el.childNodes)) walk(child);
      if (blockTags.has(tag)) ensureNewline();
    };

    for (const child of Array.from(doc.body.childNodes)) walk(child);
    return out.replace(/\r\n/g, '\n');
  } catch { return html; }
};

const splitTokens = (v: string) => v.match(/\s+|[^\s]+/g) || [];
const wrapHtml = (v: string) => (v.trim() ? `<div>${v.replace(/\n/g, '<br />')}</div>` : '<p><br></p>');
const toHtml = (content: string) => (isHtml(content) ? content : wrapHtml(esc(content || '')));
const htmlTokens = (html: string) => html.split(/(<[^>]+>)/g).filter(Boolean);

interface FormattedHtmlDiff {
  previousHtml: string;
  currentHtml: string;
  hasDiff: boolean;
  hasInsertions: boolean;
  hasDeletions: boolean;
  hasModifications: boolean;
}

const simpleDiff = (a: string, b: string): DiffSeg[] => {
  if (a === b) return [{ type: 'equal', text: a }];
  let i = 0; let j = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  while (j < a.length - i && j < b.length - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
  const out: DiffSeg[] = [];
  const pre = a.slice(0, i); const del = a.slice(i, a.length - j); const ins = b.slice(i, b.length - j); const suf = a.slice(a.length - j);
  if (pre) out.push({ type: 'equal', text: pre });
  if (del) out.push({ type: 'delete', text: del });
  if (ins) out.push({ type: 'insert', text: ins });
  if (suf) out.push({ type: 'equal', text: suf });
  return out;
};

const lcsDiff = (a: string, b: string): DiffSeg[] => {
  const ta = splitTokens(a); const tb = splitTokens(b); const n = ta.length; const m = tb.length;
  if (!n && !m) return [];
  if (!n) return [{ type: 'insert', text: tb.join('') }];
  if (!m) return [{ type: 'delete', text: ta.join('') }];
  if (n * m > 1_200_000) return simpleDiff(a, b);

  const cols = m + 1; const mat = new Uint32Array((n + 1) * (m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const idx = i * cols + j;
      mat[idx] = ta[i] === tb[j] ? mat[(i + 1) * cols + j + 1] + 1 : Math.max(mat[(i + 1) * cols + j], mat[i * cols + j + 1]);
    }
  }

  const out: DiffSeg[] = [];
  const push = (type: DiffType, text: string) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text; else out.push({ type, text });
  };

  let i = 0; let j = 0;
  while (i < n && j < m) {
    if (ta[i] === tb[j]) { push('equal', ta[i]); i++; j++; continue; }
    const down = mat[(i + 1) * cols + j];
    const right = mat[i * cols + j + 1];
    if (down >= right) { push('delete', ta[i]); i++; } else { push('insert', tb[j]); j++; }
  }
  while (i < n) { push('delete', ta[i]); i++; }
  while (j < m) { push('insert', tb[j]); j++; }
  return out;
};

type DiffChunkType = 'equal' | 'insert' | 'delete';
interface DiffChunk { type: DiffChunkType; text: string; }

const pushDiffChunk = (list: DiffChunk[], type: DiffChunkType, text: string) => {
  if (!text) return;
  const last = list[list.length - 1];
  if (last && last.type === type) {
    last.text += text;
  } else {
    list.push({ type, text });
  }
};

const wrapDiffChunk = (chunk: DiffChunk): string => {
  if (chunk.type === 'equal') return chunk.text;
  if (chunk.type === 'insert') return `<span class="diff-ins">${chunk.text}</span>`;
  return `<span class="diff-del">${chunk.text}</span>`;
};

const buildFormattedHtmlDiff = (previousContent: string, currentContent: string): FormattedHtmlDiff => {
  const previousHtml = toHtml(previousContent || '');
  const currentHtml = toHtml(currentContent || '');
  if (previousHtml === currentHtml) {
    return {
      previousHtml,
      currentHtml,
      hasDiff: false,
      hasInsertions: false,
      hasDeletions: false,
      hasModifications: false
    };
  }

  const tokenRegex = /(<[^>]+>|\s+|[^<\s]+)/g;
  const a = previousHtml.match(tokenRegex) || [];
  const b = currentHtml.match(tokenRegex) || [];
  const n = a.length;
  const m = b.length;
  if (!n && !m) return {
    previousHtml: '<p><br></p>',
    currentHtml: '<p><br></p>',
    hasDiff: false,
    hasInsertions: false,
    hasDeletions: false,
    hasModifications: false
  };
  if (!n) return {
    previousHtml: '<p><br></p>',
    currentHtml,
    hasDiff: true,
    hasInsertions: true,
    hasDeletions: false,
    hasModifications: false
  };
  if (!m) return {
    previousHtml,
    currentHtml: '<p><br></p>',
    hasDiff: true,
    hasInsertions: false,
    hasDeletions: true,
    hasModifications: false
  };

  // Protege a UI de inputs muito grandes na comparação token a token.
  if (n * m > 1_200_000) {
    return {
      previousHtml,
      currentHtml,
      hasDiff: true,
      hasInsertions: true,
      hasDeletions: true,
      hasModifications: true
    };
  }

  const cols = m + 1;
  const mat = new Uint32Array((n + 1) * (m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const idx = i * cols + j;
      mat[idx] = a[i] === b[j]
        ? mat[(i + 1) * cols + j + 1] + 1
        : Math.max(mat[(i + 1) * cols + j], mat[i * cols + j + 1]);
    }
  }

  const previousChunks: DiffChunk[] = [];
  const currentChunks: DiffChunk[] = [];
  let hasInsertions = false;
  let hasDeletions = false;
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      pushDiffChunk(previousChunks, 'equal', a[i]);
      pushDiffChunk(currentChunks, 'equal', b[j]);
      i++;
      j++;
      continue;
    }
    const down = mat[(i + 1) * cols + j];
    const right = mat[i * cols + j + 1];
    if (down >= right) {
      hasDeletions = true;
      pushDiffChunk(previousChunks, 'delete', a[i]);
      i++;
    } else {
      hasInsertions = true;
      pushDiffChunk(currentChunks, 'insert', b[j]);
      j++;
    }
  }
  while (i < n) {
    hasDeletions = true;
    pushDiffChunk(previousChunks, 'delete', a[i]);
    i++;
  }
  while (j < m) {
    hasInsertions = true;
    pushDiffChunk(currentChunks, 'insert', b[j]);
    j++;
  }

  const previousOut = previousChunks.map(wrapDiffChunk).join('');
  const currentOut = currentChunks.map(wrapDiffChunk).join('');
  return {
    previousHtml: previousOut || '<p><br></p>',
    currentHtml: currentOut || '<p><br></p>',
    hasDiff: true,
    hasInsertions,
    hasDeletions,
    hasModifications: hasInsertions && hasDeletions
  };
};

const normalizeLines = (content: string): string[] => {
  const normalized = plainText(content || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\r\n/g, '\n');

  const lines = normalized.split('\n').map(l => l.replace(/\s+$/g, ''));
  const compact: string[] = [];
  for (const line of lines) {
    if (line === '' && compact.length && compact[compact.length - 1] === '') continue;
    compact.push(line);
  }
  while (compact.length > 1 && compact[0] === '') compact.shift();
  while (compact.length > 1 && compact[compact.length - 1] === '') compact.pop();
  return compact;
};

const buildLineDiffRows = (contentA: string, contentB: string) => {
  const linesA = normalizeLines(contentA);
  const linesB = normalizeLines(contentB);
  const n = linesA.length;
  const m = linesB.length;

  const setA = new Set(linesA.filter(l => l.trim()));
  let commonNonEmpty = 0;
  for (const l of linesB) {
    if (!l.trim()) continue;
    if (setA.has(l)) { commonNonEmpty++; break; }
  }
  if (!commonNonEmpty) {
    const rows: LineDiffRow[] = [];
    const max = Math.max(n, m);
    for (let k = 0; k < max; k++) {
      const leftHas = k < n;
      const rightHas = k < m;
      const leftText = leftHas ? linesA[k] : '';
      const rightText = rightHas ? linesB[k] : '';
      const type: LineDiffType = leftHas && rightHas
        ? (leftText === rightText ? 'equal' : 'changed')
        : (leftHas ? 'delete' : 'insert');
      rows.push({
        leftNumber: leftHas ? (k + 1) : null,
        rightNumber: rightHas ? (k + 1) : null,
        leftText,
        rightText,
        type
      });
    }
    return { rows, hasDiff: rows.some(r => r.type !== 'equal') };
  }

  const cols = m + 1;
  const matrix = new Uint16Array((n + 1) * (m + 1));

  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const idx = i * cols + j;
      matrix[idx] = linesA[i] === linesB[j]
        ? (matrix[(i + 1) * cols + j + 1] + 1)
        : Math.max(matrix[(i + 1) * cols + j], matrix[i * cols + j + 1]);
    }
  }

  const rows: LineDiffRow[] = [];
  let i = 0;
  let j = 0;
  let lineA = 1;
  let lineB = 1;

  while (i < n && j < m) {
    if (linesA[i] === linesB[j]) {
      rows.push({
        leftNumber: lineA++,
        rightNumber: lineB++,
        leftText: linesA[i],
        rightText: linesB[j],
        type: 'equal'
      });
      i++;
      j++;
      continue;
    }

    const down = matrix[(i + 1) * cols + j];
    const right = matrix[i * cols + j + 1];
    if (down >= right) {
      rows.push({
        leftNumber: lineA++,
        rightNumber: null,
        leftText: linesA[i],
        rightText: '',
        type: 'delete'
      });
      i++;
    } else {
      rows.push({
        leftNumber: null,
        rightNumber: lineB++,
        leftText: '',
        rightText: linesB[j],
        type: 'insert'
      });
      j++;
    }
  }

  while (i < n) {
    rows.push({
      leftNumber: lineA++,
      rightNumber: null,
      leftText: linesA[i],
      rightText: '',
      type: 'delete'
    });
    i++;
  }

  while (j < m) {
    rows.push({
      leftNumber: null,
      rightNumber: lineB++,
      leftText: '',
      rightText: linesB[j],
      type: 'insert'
    });
    j++;
  }

  for (let idx = 0; idx < rows.length - 1; idx++) {
    if (rows[idx].type === 'delete' && rows[idx + 1].type === 'insert') {
      rows[idx].type = 'changed';
      rows[idx + 1].type = 'changed';
    }
  }

  return {
    rows,
    hasDiff: rows.some(r => r.type !== 'equal')
  };
};

const stable = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v).trim();
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (!isRecord(v)) return String(v);
  return `{${Object.keys(v).sort().map(k => `${k}:${stable(v[k])}`).join('|')}}`;
};

const formatUnknown = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return 'Não informado';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.length ? v.map(x => typeof x === 'string' ? x : stable(x)).join(', ') : '[]';
  try { return JSON.stringify(v, null, 2); } catch { return stable(v); }
};

export const DocumentVersionHistoryModal: React.FC<DocumentVersionHistoryModalProps> = ({ isOpen, onClose, documentId, documentTitle, onRestored }) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [activePane, setActivePane] = useState<ActivePane>('visualizar');
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [usersById, setUsersById] = useState<Record<string, string>>({});
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [comparePreviousId, setComparePreviousId] = useState<string | null>(null);
  const [compareCurrentId, setCompareCurrentId] = useState<string | null>(null);

  const selectedVersion = useMemo(() => versions.find(v => v.id === selectedVersionId) || null, [selectedVersionId, versions]);
  const comparePrevious = useMemo(() => versions.find(v => v.id === comparePreviousId) || null, [comparePreviousId, versions]);
  const compareCurrent = useMemo(() => versions.find(v => v.id === compareCurrentId) || null, [compareCurrentId, versions]);
  const selectedPayload = useMemo(
    () => parsePayloadWithFallback(selectedVersion?.payload || '', { title: documentTitle, status: selectedVersion?.status, updatedAt: selectedVersion?.createdAt }),
    [documentTitle, selectedVersion?.createdAt, selectedVersion?.payload, selectedVersion?.status]
  );
  const payloadPrevious = useMemo(
    () => comparePrevious ? parsePayloadWithFallback(comparePrevious.payload, { title: documentTitle, status: comparePrevious.status, updatedAt: comparePrevious.createdAt }) : null,
    [comparePrevious, documentTitle]
  );
  const payloadCurrent = useMemo(
    () => compareCurrent ? parsePayloadWithFallback(compareCurrent.payload, { title: documentTitle, status: compareCurrent.status, updatedAt: compareCurrent.createdAt }) : null,
    [compareCurrent, documentTitle]
  );
  const formattedDiff = useMemo(
    () => payloadPrevious && payloadCurrent
      ? buildFormattedHtmlDiff(payloadPrevious.content, payloadCurrent.content)
      : { previousHtml: '<p><br></p>', currentHtml: '<p><br></p>', hasDiff: false, hasInsertions: false, hasDeletions: false, hasModifications: false },
    [payloadPrevious, payloadCurrent]
  );

  const loadData = useCallback(async (): Promise<DocumentVersion[]> => {
    if (!documentId) return [];
    setLoading(true);
    try {
      const [history, usersResult] = await Promise.all([documentService.getDocumentVersionHistory(documentId), userService.getUsers('', 1, 1000)]);
      const byId: Record<string, string> = {};
      for (const user of usersResult.users || []) byId[user.id] = user.name;
      setUsersById(byId); setVersions(history || []);
      const current = (history || []).find(v => v.isCurrent);
      setSelectedVersionId(current?.id || (history?.[0]?.id ?? null));
      setComparePreviousId(history?.find(v => !v.isCurrent)?.id || (history?.[1]?.id ?? null));
      setCompareCurrentId(current?.id || (history?.[0]?.id ?? null));
      return history || [];
    } catch {
      addToast('Erro ao carregar histórico de versões', 'error');
      return [];
    } finally { setLoading(false); }
  }, [addToast, documentId]);

  useEffect(() => { if (isOpen) loadData(); }, [isOpen, loadData]);
  useEffect(() => { if (isOpen) setActivePane('visualizar'); }, [isOpen]);

  const handleCompare = () => {
    if (!comparePreviousId || !compareCurrentId) return addToast('Selecione duas versões para comparar', 'warning');
    if (comparePreviousId === compareCurrentId) return addToast('Selecione versões diferentes para comparar', 'warning');
    navigate(`/documents/${documentId}/versions/compare?v1=${comparePreviousId}&v2=${compareCurrentId}`);
    onClose();
  };

  const handleRestore = async (versionId: string) => {
    const versionToRestore = versions.find(v => v.id === versionId);
    if (!versionToRestore) return;
    if (versionToRestore.isCurrent) return addToast('Esta já é a versão atual', 'info');
    if (!window.confirm(`Restaurar a versão v${versionToRestore.versionLabel} como versão atual?`)) return;
    setRestoreLoading(true);
    try {
      await documentService.restoreDocumentVersion(documentId, versionId);
      addToast('Versão restaurada com sucesso', 'success');
      const refreshed = await loadData();
      const latest = refreshed.find(v => v.isCurrent) || null;
      if (latest) onRestored?.(latest);
    } catch { addToast('Erro ao restaurar versão', 'error'); }
    finally { setRestoreLoading(false); }
  };

  const renderAttachments = (list: ParsedAttachment[]) => list.length ? (
    <ul className="space-y-1">{list.map((a, i) => <li key={`${a.name}-${i}`} className="text-sm text-gray-800 break-all">{a.url ? <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{a.name}</a> : a.name}</li>)}</ul>
  ) : <span className="text-sm text-gray-500">Nenhum arquivo</span>;

  const renderCover = (url: string) => url ? (
    <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all text-sm">Visualizar imagem</a>
  ) : <span className="text-sm text-gray-500">Não informado</span>;

  const renderContent = (content: string) => {
    if (!content) return <span className="text-sm text-gray-500">Sem conteúdo</span>;
    const value = isHtml(content) ? content : wrapHtml(esc(content));
    return <div className="quill-readonly-content"><ReactQuill value={value} readOnly theme="snow" modules={readOnlyModules} /></div>;
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto"><div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <Dialog.Panel className="relative w-full max-w-6xl transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
              <style>{`.quill-readonly-content .ql-toolbar.ql-snow{display:none}.quill-readonly-content .ql-container.ql-snow{border:0;font-size:.875rem}.quill-readonly-content .ql-editor{padding:0;min-height:80px;max-height:44vh;overflow-y:auto;white-space:pre-wrap;word-break:break-word}.quill-readonly-content .diff-del{background:#fef2f2;border:1px solid #fca5a5;border-radius:4px;text-decoration:line-through;padding:0 2px}.quill-readonly-content .diff-ins{background:#ecfdf5;border:1px solid #86efac;border-radius:4px;padding:0 2px}`}</style>
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4"><div><Dialog.Title as="h3" className="text-lg font-medium text-gray-900">Histórico de versões</Dialog.Title><p className="text-sm text-gray-500">{documentTitle}</p></div><button type="button" className="rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100" onClick={onClose} aria-label="Fechar"><XMarkIcon className="h-6 w-6" /></button></div>
              <div className="flex h-[70vh] min-h-[520px]">
                <div className="w-full max-w-sm border-r border-gray-200">
                  <div className="flex items-center justify-between px-4 py-3"><div className="text-sm font-medium text-gray-700">Versões</div>{loading && <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-500" />}</div>
                  <div className="h-full overflow-y-auto px-2 pb-4">
                    {versions.length === 0 && !loading && <div className="px-3 py-4 text-sm text-gray-500">Nenhuma versão encontrada</div>}
                    {versions.map(v => {
                      const sel = v.id === selectedVersionId; const user = usersById[v.createdBy] || v.createdBy;
                      return <button key={v.id} type="button" onClick={() => { setSelectedVersionId(v.id); setActivePane('visualizar'); }} className={`w-full rounded-lg px-3 py-3 text-left transition-colors ${sel ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}><div className="flex items-start justify-between gap-2"><div className="min-w-0"><div className="flex items-center gap-2"><span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">v{v.versionLabel}</span>{v.isCurrent && <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Atual</span>}</div><div className="mt-1 text-xs text-gray-600 truncate">{user}</div><div className="text-xs text-gray-500">{formatDateTime(v.createdAt)}</div></div><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusClassName(v.status)}`}>{statusLabel(v.status)}</span></div></button>;
                    })}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3"><div className="flex items-center gap-2"><button type="button" onClick={() => setActivePane('visualizar')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${activePane === 'visualizar' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}>Visualizar</button><button type="button" onClick={() => setActivePane('comparar')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${activePane === 'comparar' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}>Comparar</button></div>{selectedVersion && <button type="button" disabled={restoreLoading} onClick={() => handleRestore(selectedVersion.id)} className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Restaurar</button>}</div>

                  {activePane === 'visualizar' && <div className="h-full overflow-y-auto px-6 py-4">{!selectedVersion ? <div className="text-sm text-gray-500">Selecione uma versão</div> : <div className="space-y-4"><div className="flex items-start justify-between gap-4"><div><div className="flex items-center gap-2"><div className="text-lg font-semibold text-gray-900">v{selectedVersion.versionLabel}</div>{selectedVersion.isCurrent && <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Atual</span>}</div><div className="text-sm text-gray-600">{usersById[selectedVersion.createdBy] || selectedVersion.createdBy} · {formatDateTime(selectedVersion.createdAt)}</div></div><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusClassName(selectedVersion.status)}`}>{statusLabel(selectedVersion.status)}</span></div><div className="rounded-lg border border-gray-200 bg-white p-4"><div className="text-sm font-medium text-gray-700 mb-2">Conteúdo</div><div className="max-h-[44vh] overflow-y-auto rounded-md bg-gray-50 p-3">{renderContent(selectedPayload.content || selectedVersion.payload)}</div></div></div>}</div>}

                  {activePane === 'comparar' && <div className="h-full overflow-y-auto px-6 py-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div><label className="block text-xs font-medium text-gray-600 mb-1">Comparar:</label><select value={comparePreviousId || ''} onChange={e => setComparePreviousId(e.target.value || null)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"><option value="">Selecione</option>{versions.map(v => <option key={v.id} value={v.id}>v{v.versionLabel} {v.isCurrent ? '(Atual)' : ''}</option>)}</select></div>
                      <div><label className="block text-xs font-medium text-gray-600 mb-1">Com:</label><select value={compareCurrentId || ''} onChange={e => setCompareCurrentId(e.target.value || null)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"><option value="">Selecione</option>{versions.map(v => <option key={v.id} value={v.id}>v{v.versionLabel} {v.isCurrent ? '(Atual)' : ''}</option>)}</select></div>
                      <div className="flex gap-2"><button type="button" disabled={!comparePreviousId || !compareCurrentId} onClick={handleCompare} className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">Comparar</button></div>
                    </div>
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
                      Selecione as versões anterior e atual e clique em <strong>Comparar</strong> para abrir a visualização completa de diferenças em tela cheia.
                    </div>
                  </div>}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div></div>
      </Dialog>
    </Transition.Root>
  );
};
